import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  BrowserAuthError,
} from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import { clearMemberSession, getMemberSession, memberToken } from "./memberSession";
import { clearSandboxSession, sandboxToken } from "./sandboxSession";

let msalInstance: PublicClientApplication | null = null;

export function setMsalInstance(instance: PublicClientApplication): void {
  msalInstance = instance;
}

const TOKEN_REQUEST = { scopes: ["User.Read"] };

const REDIRECT_FALLBACK_CODES = new Set([
  "monitor_window_timeout",
  "block_iframe_reload",
  "iframe_closed_prematurely",
  "silent_sso_error",
  "no_tokens_found",
  "no_account_in_silent_request",
  "interaction_required",
  "consent_required",
  "login_required",
]);

function shouldFallbackToRedirect(err: unknown): boolean {
  if (err instanceof InteractionRequiredAuthError) return true;
  if (err instanceof BrowserAuthError) return REDIRECT_FALLBACK_CODES.has(err.errorCode);
  if (err && typeof err === "object" && "errorCode" in err) {
    const code = (err as { errorCode?: unknown }).errorCode;
    if (typeof code === "string" && REDIRECT_FALLBACK_CODES.has(code)) return true;
  }
  return false;
}

const REDIRECT_GUARD_KEY = "sherloxaware_authfetch_redirect_at";
const REDIRECT_COOLDOWN_MS = 60_000;

function recentlyRedirected(): boolean {
  try {
    const raw = sessionStorage.getItem(REDIRECT_GUARD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < REDIRECT_COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markRedirected(): void {
  try { sessionStorage.setItem(REDIRECT_GUARD_KEY, String(Date.now())); } catch { /* ignore */ }
}

let pendingToken: Promise<string> | null = null;
let lastForceRefreshAt = 0;

async function acquireIdTokenRaw(forceRefresh: boolean): Promise<string> {
  if (!msalInstance) {
    throw new Error("authFetch: MSAL instance not set – call setMsalInstance() in main.tsx");
  }
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) {
    throw new Error("authFetch: no active MSAL account – user must log in");
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ ...TOKEN_REQUEST, account, forceRefresh });
    if (!result.idToken) throw new Error("authFetch: idToken missing in token response");
    try { sessionStorage.removeItem(REDIRECT_GUARD_KEY); } catch { /* ignore */ }
    return result.idToken;
  } catch (err) {
    if (shouldFallbackToRedirect(err) && !recentlyRedirected()) {
      markRedirected();
      await msalInstance.acquireTokenRedirect({ ...loginRequest, ...TOKEN_REQUEST, account });
      throw new Error("authFetch: redirecting to Microsoft – token will be available after redirect");
    }
    throw err;
  }
}

async function acquireIdToken(forceRefresh = false): Promise<string> {
  if (forceRefresh) {
    if (Date.now() - lastForceRefreshAt < 1500 && pendingToken) {
      return pendingToken;
    }
    lastForceRefreshAt = Date.now();
    pendingToken = acquireIdTokenRaw(true).finally(() => { pendingToken = null; });
    return pendingToken;
  }
  if (pendingToken) return pendingToken;
  return acquireIdTokenRaw(false);
}

// Performance-Layer für authFetch:
//
// 1. Dedupe in-flight GETs: gleichzeitige Anfragen an dieselbe URL teilen sich
//    einen einzigen Netzwerk-Request.
// 2. Kurzlebiger Response-Cache (10s) für GETs: nach Navigation zur selben Seite
//    oder Polling-Refresh wird gecachte Antwort sofort geliefert, ohne erneuten
//    Round-Trip zum Backend.
// 3. Cache wird bei nicht-GET-Requests (POST/PUT/PATCH/DELETE) gegen den passenden
//    Pfad invalidiert, damit nach Mutationen keine stale Daten erscheinen.
//
// Jeder Caller bekommt einen geklonten Response, sodass .json()/.body unabhängig
// konsumiert werden können.

const inflight = new Map<string, Promise<Response>>();
const cache = new Map<string, { response: Response; expiresAt: number }>();
const CACHE_TTL_MS = 10_000;

// 4. Persistenter GET-Cache (localStorage, stale-while-revalidate): die letzte
//    bekannte Antwort wird beim nächsten App-Start sofort geliefert, während im
//    Hintergrund frisch nachgeladen wird. Dadurch erscheinen Dashboard-Daten
//    bei Wiederbesuchen ohne Wartezeit auf Vercel-Cold-Starts.
//    Keys enthalten die volle URL (inkl. tenantId/email) und sind damit
//    automatisch tenant-/user-gescoped. Beim Logout via clearPersistedApiCache()
//    löschen (Layout.tsx + AdminLayout.tsx).
const PERSIST_PREFIX = "sherloxaware_apicache_v1:";
// Daten (Fortschritt, Kampagnen, Nutzerlisten): kurz halten. Layout.tsx laedt die
// sichtbaren Zahlen ohnehin alle 90 Sekunden nach, ein zu alter Stand faellt auf.
const PERSIST_TTL_MS = 15 * 60_000;
// Konfiguration (Admin-Liste, Sammelaccount-Status) aendert sich selten, haelt aber
// den Start auf: genau diese zwei Antworten entscheiden, was ueberhaupt gerendert
// wird. Sie duerfen deshalb deutlich laenger aus dem Cache kommen — wer davon
// abhaengt, holt sich ueber authGetSwr zusaetzlich die frische Antwort nach.
const PERSIST_TTL_LONG_MS = 2 * 60 * 60_000;
const PERSIST_LONG_TTL_PATTERNS = ["/api/entitlements", "shared=status"];
const PERSIST_MAX_BODY_CHARS = 200_000;

function persistTtlFor(key: string): number {
  return PERSIST_LONG_TTL_PATTERNS.some((pattern) => key.includes(pattern))
    ? PERSIST_TTL_LONG_MS
    : PERSIST_TTL_MS;
}

function shouldPersist(key: string): boolean {
  // Mitglieder eines Sammelaccounts bekommen keinen localStorage-Cache: der
  // überlebte den Sitzungswechsel am gemeinsamen Gerät.
  if (getMemberSession()) return false;
  return key.includes("/api/") && key.includes("tenantId=");
}

function readPersisted(key: string): string | null {
  try {
    const raw = localStorage.getItem(PERSIST_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { t?: number; b?: string };
    if (typeof parsed.t !== "number" || typeof parsed.b !== "string") return null;
    if (Date.now() - parsed.t > persistTtlFor(key)) {
      localStorage.removeItem(PERSIST_PREFIX + key);
      return null;
    }
    return parsed.b;
  } catch {
    return null;
  }
}

// Synchroner Zugriff auf den persistenten GET-Cache für React-Lazy-Initializer
// (useState(() => ...)), damit z.B. das Kunden-Logo schon im ersten Render
// aus dem Cache steht statt erst nach dem async Fetch nachzuspringen.
export function readPersistedJson(url: string): unknown | null {
  const raw = readPersisted(`GET ${url}`);
  if (raw === null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

async function writePersisted(key: string, response: Response): Promise<void> {
  try {
    const body = await response.clone().text();
    if (body.length > PERSIST_MAX_BODY_CHARS) return;
    localStorage.setItem(PERSIST_PREFIX + key, JSON.stringify({ t: Date.now(), b: body }));
  } catch {
    // Quota voll o.ä. – der Cache ist optional
  }
}

function invalidatePersistedForPath(path: string): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PERSIST_PREFIX) && k.includes(path)) localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}

export function clearPersistedApiCache(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PERSIST_PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    // ignore
  }
}

function methodOf(input: RequestInfo | URL, init: RequestInit): string {
  if (init.method) return init.method.toUpperCase();
  if (typeof Request !== "undefined" && input instanceof Request) return input.method.toUpperCase();
  return "GET";
}

function urlOf(input: RequestInfo | URL): string {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.toString();
  if (typeof Request !== "undefined" && input instanceof Request) return input.url;
  return "";
}

function pathOnly(url: string): string {
  try {
    const u = new URL(url, "http://x");
    return u.pathname;
  } catch {
    return url.split("?")[0] ?? url;
  }
}

function dedupeKey(input: RequestInfo | URL, init: RequestInit): string | null {
  if (methodOf(input, init) !== "GET") return null;
  if (init.body) return null;
  if (init.signal) return null;
  const url = urlOf(input);
  if (!url) return null;
  // Am Sammelaccount teilen sich mehrere Personen denselben Browser. Ohne die
  // Person im Schlüssel bekäme die nächste Schicht die Antworten der vorherigen.
  const member = getMemberSession();
  return member ? `GET[${member.email}] ${url}` : `GET ${url}`;
}

function invalidateCacheForPath(path: string) {
  for (const key of cache.keys()) {
    if (key.includes(path)) cache.delete(key);
  }
  invalidatePersistedForPath(path);
}

// Netzwerk-GET mit In-flight-Dedupe; aktualisiert Memory- und Persist-Cache.
function startNetworkGet(key: string, send: (forceRefresh: boolean) => Promise<Response>): Promise<Response> {
  const existing = inflight.get(key);
  if (existing) return existing;
  const fresh = (async () => {
    const first = await send(false);
    if (first.status !== 401) return first;
    try { return await send(true); } catch { return first; }
  })();
  inflight.set(key, fresh);
  fresh.then(response => {
    if (response.ok) {
      cache.set(key, { response: response.clone(), expiresAt: Date.now() + CACHE_TTL_MS });
      if (shouldPersist(key)) void writePersisted(key, response);
    }
  }).catch(() => { /* cache nur OK-Responses */ });
  fresh.finally(() => {
    setTimeout(() => { if (inflight.get(key) === fresh) inflight.delete(key); }, 500);
  });
  return fresh;
}

// Sammelaccount: läuft die Mitgliedssitzung ab oder wird sie ungültig, antwortet
// das Backend mit 401 statt stillschweigend auf das gemeinsame Konto zurückzufallen.
// Dann muss die Sitzung weg und die Anmeldemaske wieder her – sonst schriebe die
// Person ihren Fortschritt unbemerkt auf das Sammelkonto.
let memberRecoveryRunning = false;
async function handleMemberSessionLoss(response: Response): Promise<void> {
  if (response.status !== 401 || memberRecoveryRunning || !getMemberSession()) return;
  let body = "";
  try { body = await response.clone().text(); } catch { return; }
  if (!body.includes("member_session_invalid")) return;
  memberRecoveryRunning = true;
  clearMemberSession();
  clearPersistedApiCache();
  cache.clear();
  try { window.location.reload(); } catch { /* ignore */ }
}

// Sandbox-Token laufen nach einer Stunde ab und werden nicht erneuert. Danach
// ist die Sitzung weg und der nächste Aufruf bekommt 401 – die Seite lädt neu
// und zeigt den Hinweis, dass die Sandbox im GA-Portal neu geöffnet werden muss.
let sandboxRecoveryRunning = false;
async function handleSandboxSessionLoss(response: Response): Promise<void> {
  if (response.status !== 401 || sandboxRecoveryRunning) return;
  let body = "";
  try { body = await response.clone().text(); } catch { return; }
  if (!body.includes("sandbox_session_invalid")) return;
  sandboxRecoveryRunning = true;
  clearSandboxSession();
  clearPersistedApiCache();
  cache.clear();
  try { window.location.reload(); } catch { /* ignore */ }
}

type InternalOptions = {
  /**
   * Den persistenten Cache ueberspringen und wirklich das Netz fragen. Nur
   * authGetSwr nutzt das: dort ist der alte Stand schon an den Aufrufer
   * gegangen, gebraucht wird jetzt die frische Antwort.
   */
  skipPersistedCache?: boolean;
  /** Auch den 10-Sekunden-Speicher im Arbeitsspeicher auslassen (authFetchFresh). */
  skipMemoryCache?: boolean;
};

async function authFetchInternal(
  input: RequestInfo | URL,
  init: RequestInit,
  options: InternalOptions,
): Promise<Response> {
  const send = async (forceRefresh: boolean): Promise<Response> => {
    const headers = new Headers(init.headers ?? {});

    // Sandbox-Sitzung: eigener Zugangsweg ohne Microsoft-Anmeldung. Der Server
    // prüft ausschließlich diesen Header und fällt nicht auf Entra zurück
    // (siehe requireAuth in api/_auth.js) – deshalb hier auch kein
    // Authorization-Header und kein MSAL-Aufruf, der ohne Konto scheitern würde.
    const sandbox = sandboxToken();
    if (sandbox) {
      headers.set("X-Aware-Sandbox", sandbox);
      // Auch in der Sandbox gibt es Sammelaccounts: Wer sich dort mit seiner Nummer
      // angemeldet hat, muss dieselbe Mitgliedssitzung mitschicken wie ein echter
      // Kunde – sonst antwortet der Server zu Recht mit 401.
      const sandboxMember = memberToken();
      if (sandboxMember) headers.set("X-Aware-Member", sandboxMember);
      const sandboxResponse = await fetch(input, { ...init, headers });
      await handleSandboxSessionLoss(sandboxResponse);
      await handleMemberSessionLoss(sandboxResponse);
      return sandboxResponse;
    }

    const token = await acquireIdToken(forceRefresh);
    headers.set("Authorization", `Bearer ${token}`);
    const member = memberToken();
    if (member) headers.set("X-Aware-Member", member);
    const response = await fetch(input, { ...init, headers });
    await handleMemberSessionLoss(response);
    return response;
  };

  const key = dedupeKey(input, init);

  // Nicht-GET: erstmal cache invalidieren, danach normal senden.
  if (!key) {
    const method = methodOf(input, init);
    if (method !== "GET") invalidateCacheForPath(pathOnly(urlOf(input)));
    const first = await send(false);
    if (first.status !== 401) return first;
    try { return await send(true); } catch { return first; }
  }

  // GET mit Cache-Hit innerhalb TTL → sofort liefern.
  const cached = cache.get(key);
  if (cached && !options.skipMemoryCache && Date.now() < cached.expiresAt) {
    return cached.response.clone();
  }

  // Persistenter Cache: letzte bekannte Antwort sofort liefern und im
  // Hintergrund revalidieren (nächster Mount/Besuch bekommt frische Daten).
  if (shouldPersist(key) && !options.skipPersistedCache) {
    const persisted = readPersisted(key);
    if (persisted !== null) {
      void startNetworkGet(key, send);
      return new Response(persisted, { status: 200, headers: { "Content-Type": "application/json" } });
    }
  }

  return startNetworkGet(key, send).then(r => r.clone());
}

export function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  return authFetchInternal(input, init, {});
}

/**
 * GET am persistenten Cache vorbei. Fuer Zahlen, die ein anderer Admin gerade
 * geaendert haben kann (Schulungsstand): authFetch gaebe dort zuerst den alten
 * Stand aus dem localStorage zurueck.
 */
export function authFetchFresh(url: string): Promise<Response> {
  return authFetchInternal(url, {}, { skipPersistedCache: true, skipMemoryCache: true });
}

/**
 * GET fuer die zwei Abfragen, die den App-Start blockieren (Admin-Liste,
 * Sammelaccount-Status).
 *
 * `onData` kommt bis zu zweimal: zuerst sofort mit der zuletzt bekannten Antwort
 * aus dem localStorage-Cache (`fresh === false`), danach noch einmal mit der vom
 * Server bestaetigten (`fresh === true`), auch wenn sie gleich lautet — Aufrufer,
 * die daraus eine Zugriffsentscheidung ableiten, brauchen genau diese
 * Bestaetigung. Der zweite Aufruf ist der Grund, warum es diese
 * Funktion gibt: authFetch liefert dem Aufrufer den alten Stand und schreibt das
 * frische Ergebnis nur in den Cache. Bei kurzer TTL faellt das nicht auf, bei den
 * zwei Stunden aus PERSIST_TTL_LONG_MS schon.
 *
 * Rueckgabe: Abbruchfunktion fuer das useEffect-Cleanup.
 */
export function authGetSwr(url: string, onData: (data: unknown, fresh: boolean) => void): () => void {
  let cancelled = false;
  const key = dedupeKey(url, {});
  const stale = key && shouldPersist(key) ? readPersisted(key) : null;

  if (stale !== null) {
    try {
      onData(JSON.parse(stale), false);
    } catch {
      // Kaputter Cache-Eintrag – die Netzantwort unten korrigiert ihn.
    }
  }

  authFetchInternal(url, {}, { skipPersistedCache: true })
    .then((res) => (res.ok ? res.text() : null))
    .then((body) => {
      if (cancelled || body === null) return;
      // Auch eine inhaltlich unveraenderte Antwort wird gemeldet: Aufrufer, die
      // zwischen Cache und frisch authentifizierter Antwort unterscheiden
      // (Sammelaccount-Pruefung in SharedAccountGate), warten sonst ewig auf
      // eine Bestaetigung, die nie kommt.
      try {
        onData(JSON.parse(body), true);
      } catch {
        // Keine JSON-Antwort – der Aufrufer behaelt den alten Stand.
      }
    })
    .catch(() => {
      // Offline o.ae.: der aus dem Cache gelieferte Stand bleibt stehen.
    });

  return () => { cancelled = true; };
}
